import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  ShoppingCart, 
  Plus, 
  Trash2, 
  RefreshCw,
  Download,
  Printer,
  ChefHat,
  Check,
  X,
  Sparkles,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Local storage keys
const SHOPPING_LIST_KEY = 'calm_keeper_shopping_list';
const MEAL_CALENDAR_KEY = 'calm_keeper_meal_calendar';

const getShoppingList = () => {
  const stored = localStorage.getItem(SHOPPING_LIST_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveShoppingList = (list) => {
  localStorage.setItem(SHOPPING_LIST_KEY, JSON.stringify(list));
};

const getMealCalendar = () => {
  const stored = localStorage.getItem(MEAL_CALENDAR_KEY);
  return stored ? JSON.parse(stored) : {};
};

// Categories for organizing shopping list
const categories = [
  { id: 'produce', label: 'Produce', emoji: '🥬' },
  { id: 'protein', label: 'Protein', emoji: '🥩' },
  { id: 'dairy', label: 'Dairy', emoji: '🥛' },
  { id: 'pantry', label: 'Pantry', emoji: '🥫' },
  { id: 'frozen', label: 'Frozen', emoji: '🧊' },
  { id: 'other', label: 'Other', emoji: '📦' },
];

const ShoppingItem = ({ item, onToggle, onRemove, onUpdateCategory }) => {
  const category = categories.find(c => c.id === item.category) || categories[5];
  
  return (
    <div className={`
      flex items-center gap-3 p-3 rounded-lg border transition-all group
      ${item.checked 
        ? 'bg-paper-aged border-stone-200 opacity-60' 
        : 'bg-paper-light border-stone-200 hover:shadow-sm'
      }
    `}>
      <Checkbox
        checked={item.checked}
        onCheckedChange={() => onToggle(item.id)}
        className="border-brown"
      />
      
      <span className="text-lg">{category.emoji}</span>
      
      <span className={`
        flex-1 font-sans
        ${item.checked ? 'line-through text-ink-500' : 'text-ink-900'}
      `}>
        {item.name}
        {item.quantity && (
          <span className="text-ink-500 text-sm ml-2">({item.quantity})</span>
        )}
      </span>
      
      {item.fromMeal && (
        <Badge className="bg-sage/10 text-sage-dark text-xs border border-sage/20">
          {item.fromMeal}
        </Badge>
      )}
      
      <Button
        onClick={() => onRemove(item.id)}
        variant="ghost"
        size="icon"
        className="h-8 w-8 opacity-0 group-hover:opacity-100 text-ink-500 hover:text-destructive"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const ShoppingList = () => {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('other');
  
  useEffect(() => {
    setItems(getShoppingList());
  }, []);
  
  const handleAddItem = () => {
    if (!newItem.trim()) return;
    
    const item = {
      id: Date.now(),
      name: newItem.trim(),
      category: selectedCategory,
      checked: false,
      createdAt: new Date().toISOString(),
    };
    
    const newItems = [item, ...items];
    setItems(newItems);
    saveShoppingList(newItems);
    setNewItem('');
    toast.success('Item added!');
  };
  
  const handleToggle = (id) => {
    const newItems = items.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setItems(newItems);
    saveShoppingList(newItems);
  };
  
  const handleRemove = (id) => {
    const newItems = items.filter(item => item.id !== id);
    setItems(newItems);
    saveShoppingList(newItems);
  };
  
  const handleClearChecked = () => {
    const newItems = items.filter(item => !item.checked);
    setItems(newItems);
    saveShoppingList(newItems);
    toast.success('Checked items cleared!');
  };
  
  const handleClearAll = () => {
    setItems([]);
    saveShoppingList([]);
    toast.success('Shopping list cleared!');
  };
  
  // Generate shopping list from meal calendar using AI
  const handleGenerateFromMeals = async () => {
    const mealCalendar = getMealCalendar();
    const meals = [];
    
    // Get this week's meals
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dateKey = date.toISOString().split('T')[0];
      
      const dayMeals = mealCalendar[dateKey];
      if (dayMeals) {
        Object.entries(dayMeals).forEach(([type, meal]) => {
          if (meal) meals.push({ meal, type, date: dateKey });
        });
      }
    }
    
    if (meals.length === 0) {
      toast.error('No meals planned this week. Add meals to your calendar first!');
      return;
    }
    
    setGenerating(true);
    
    try {
      // Use AI to generate shopping list from meals
      const mealList = meals.map(m => m.meal).join(', ');
      
      const response = await axios.post(`${API}/resources/generate-shopping-list`, {
        meals: mealList,
      });
      
      if (response.data.items && response.data.items.length > 0) {
        const newItems = response.data.items.map((item, idx) => ({
          id: Date.now() + idx,
          name: item.name,
          category: item.category || 'other',
          quantity: item.quantity,
          checked: false,
          fromMeal: item.fromMeal || 'Weekly meals',
          createdAt: new Date().toISOString(),
        }));
        
        // Merge with existing items, avoiding duplicates
        const existingNames = items.map(i => i.name.toLowerCase());
        const uniqueNewItems = newItems.filter(
          item => !existingNames.includes(item.name.toLowerCase())
        );
        
        const mergedItems = [...uniqueNewItems, ...items];
        setItems(mergedItems);
        saveShoppingList(mergedItems);
        
        toast.success(`Added ${uniqueNewItems.length} items from your meal plan!`);
      }
    } catch (err) {
      console.error('Error generating shopping list:', err);
      toast.error('Could not generate shopping list. Try adding items manually.');
    } finally {
      setGenerating(false);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  // Group items by category
  const groupedItems = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category === cat.id),
  })).filter(cat => cat.items.length > 0);
  
  const uncheckedCount = items.filter(i => !i.checked).length;
  const checkedCount = items.filter(i => i.checked).length;
  
  return (
    <div className="space-y-6" data-testid="shopping-list-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Shopping List
          </h2>
          <p className="text-ink-500 font-accent italic">
            {uncheckedCount} items to get • {checkedCount} checked off 🛒
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
          <ShoppingCart className="w-6 h-6 text-sage" />
        </div>
      </div>
      
      {/* Generate from meals */}
      <Card className="p-4 bg-brown/5 border border-brown/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brown/10 flex items-center justify-center">
            <ChefHat className="w-5 h-5 text-brown" />
          </div>
          <div className="flex-1">
            <p className="font-serif text-ink-900">Generate from Meal Calendar</p>
            <p className="text-xs text-ink-500 font-accent italic">
              AI will create a shopping list from your planned meals
            </p>
          </div>
          <Button
            data-testid="generate-shopping-list-btn"
            onClick={handleGenerateFromMeals}
            disabled={generating}
            className="vintage-btn btn-brown rounded-lg"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate
          </Button>
        </div>
      </Card>
      
      {/* Add item */}
      <Card className="p-4 bg-paper-light shadow-lifted border border-stone-200">
        <div className="flex gap-2 mb-3">
          <Input
            data-testid="shopping-item-input"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
            placeholder="Add an item..."
            className="flex-1 border-stone-300"
          />
          <Button
            data-testid="shopping-add-btn"
            onClick={handleAddItem}
            disabled={!newItem.trim()}
            className="btn-sage"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Category selector */}
        <div className="flex gap-1 flex-wrap">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              className={`text-xs ${selectedCategory === cat.id ? 'bg-brown text-white' : 'border-stone-300'}`}
            >
              {cat.emoji} {cat.label}
            </Button>
          ))}
        </div>
      </Card>
      
      {/* Shopping list by category */}
      {groupedItems.length > 0 ? (
        <div className="space-y-4 print-container">
          {/* Print header */}
          <div className="hidden print:block text-center mb-4">
            <h1 className="font-serif text-xl">Shopping List</h1>
            <p className="text-sm text-ink-500">{new Date().toLocaleDateString()}</p>
          </div>
          
          {groupedItems.map((group) => (
            <div key={group.id} className="space-y-2">
              <h3 className="font-serif text-lg text-ink-900 flex items-center gap-2">
                <span>{group.emoji}</span>
                {group.label}
                <span className="text-sm text-ink-500 font-sans">({group.items.length})</span>
              </h3>
              <div className="space-y-2">
                {group.items.map((item) => (
                  <ShoppingItem
                    key={item.id}
                    item={item}
                    onToggle={handleToggle}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center bg-paper-light shadow-lifted border border-stone-200">
          <ShoppingCart className="w-12 h-12 text-ink-300 mx-auto mb-4" />
          <p className="text-ink-500 font-accent italic">
            Your shopping list is empty. Add items or generate from your meal plan!
          </p>
        </Card>
      )}
      
      {/* Actions */}
      {items.length > 0 && (
        <div className="flex gap-3 flex-wrap no-print">
          <Button
            onClick={handlePrint}
            variant="outline"
            className="border-stone-300"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print List
          </Button>
          
          {checkedCount > 0 && (
            <Button
              onClick={handleClearChecked}
              variant="outline"
              className="border-stone-300 text-sage"
            >
              <Check className="w-4 h-4 mr-2" />
              Clear Checked ({checkedCount})
            </Button>
          )}
          
          <Button
            onClick={handleClearAll}
            variant="outline"
            className="border-destructive/30 text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>
      )}
      
      {/* Tip */}
      <Card className="p-4 bg-warmth-sand border border-stone-300 no-print">
        <p className="text-sm text-ink-700 font-handwritten text-lg text-center">
          🛒 Tip: Check items off as you shop, then clear checked items when done!
        </p>
      </Card>
    </div>
  );
};

export default ShoppingList;
